<script lang="ts">
import { RouteNames } from "@/utils/constants.ts";

export function getRouteOnboardBusinessOwners() {
  return {
    name: RouteNames.ONBOARDING_BUSINESS_OWNERS
  };
}
</script>

<script setup lang="ts">
import { ref } from "vue";
import { useRouter } from "vue-router";
import CardLayout from "@/components/layout/CardLayout.vue";
import { TextInput } from "@/components/form/index.ts";
import InputSelect from "@/components/form/InputSelect.vue";
import BasicFormTitle from "@/components/basic/BasicFormTitle.vue";
import { getRouteXDashboard } from "@/views/Auth/Dashboard.vue";
import { getRouteOnboardBusinessQuestionnaire } from "@/views/Onboarding/OnboardingBusinessQuestionnaire.vue";
import Countries from "@/libs/core/countries.ts";
import { Icon } from "@iconify/vue";
import { IconCloudUpload } from "@iconify-prerendered/vue-carbon";
import { PersonService } from "@/services";
import { usePersonStore } from "@/store";
import { error } from "@/utils/toast.ts";

const router = useRouter();
const personService = new PersonService();
const personStore = usePersonStore();
const isLoading = ref(false);

// Prepare countries list
const countries: any = {};
for (const i in Countries) {
  const country = Countries[i];
  countries[country.code3] = country;
}

// Owner (UBO) Data
interface IOwner {
  id: string;
  isOwner: boolean;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  personalCode: string;
  citizenship: string;
  isPep: boolean;
  isUS: boolean;
  sharesVotingRights: string;
  taxCountry: string;
  taxNumber: string;
  addressLine: string;
  city: string;
  country: string;
  postcode: string;
}

// Director Data
interface IDirector {
  id: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  beneficialOwner: string;
}

// Signer Data
interface ISigner {
  id: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  beneficialOwner: string;
}

const owners = ref<IOwner[]>([
  {
    id: '1',
    isOwner: false,
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    personalCode: '',
    citizenship: 'LVA',
    isPep: false,
    isUS: false,
    sharesVotingRights: '',
    taxCountry: 'LVA',
    taxNumber: '',
    addressLine: '',
    city: '',
    country: 'LVA',
    postcode: ''
  }
]);

const directors = ref<IDirector[]>([
  {
    id: '1',
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    beneficialOwner: ''
  }
]);

const signers = ref<ISigner[]>([
  {
    id: '1',
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    beneficialOwner: ''
  }
]);

function addOwner() {
  owners.value.push({
    id: Date.now().toString(),
    isOwner: false,
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    personalCode: '',
    citizenship: 'LVA',
    isPep: false,
    isUS: false,
    sharesVotingRights: '',
    taxCountry: 'LVA',
    taxNumber: '',
    addressLine: '',
    city: '',
    country: 'LVA',
    postcode: ''
  });
}

function addDirector() {
  directors.value.push({
    id: Date.now().toString(),
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    beneficialOwner: ''
  });
}

function addSigner() {
  signers.value.push({
    id: Date.now().toString(),
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    beneficialOwner: ''
  });
}

async function submitForm() {
  isLoading.value = true;
  try {
    const ownersData = {
      owners: owners.value,
      directors: directors.value,
      signers: signers.value
    };

    console.log("Saving business owners data...");
    console.log("Business owners data:", ownersData);
    await personService.createBusinessOwners("business-owners", ownersData);
    console.log("Business owners data saved");

    // Refresh person state to get updated data
    await personStore.getState();

    // Navigate to Dashboard - business onboarding is complete
    await router.push(getRouteXDashboard());
  } catch (e) {
    console.error("Error submitting business owners:", e);
    error("Failed to save business information");
  } finally {
    isLoading.value = false;
  }
}

const goBack = () => {
  router.push(getRouteOnboardBusinessQuestionnaire());
};

const isFormValid = () => {
  return owners.value.every(owner =>
    owner.firstName && owner.lastName && owner.dateOfBirth
  );
};
</script>

<template>
  <CardLayout
    title="Business Account"
    subtitle="Please provide detailed information about natural person(s) who ultimately holds or controls the Customer by directly or indirectly holding or controlling 25 percent or more shares, voting rights"
    heading="Business Owners"
    :max-steps="4"
    :current-step="4"
    :next="submitForm"
    :back="goBack"
    :isAllowedNext="isFormValid()"
  >
    <div class="flex flex-col w-full gap-8">
      <!-- Owners (UBO) -->
      <div v-for="(owner) in owners" :key="owner.id" class="flex flex-col gap-6">
        <div class="px-4 md:px-0">
          <BasicFormTitle>
            Owner (UBO)
          </BasicFormTitle>
        </div>

        <div class="px-4 md:px-0">
          <label class="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              v-model="owner.isOwner"
              class="w-4 h-4 text-blue-500 border-gray-300 rounded focus:ring-blue-500"
            />
            <span class="text-sm text-gray-700">I am the Owner</span>
          </label>
        </div>

        <!-- Personal Details -->
        <div class="px-4 md:px-0">
          <h4 class="text-sm font-semibold text-gray-900 mb-4">Personal Details</h4>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-6 px-4 md:px-0">
          <div class="flex flex-col gap-y-2">
            <label class="text-sm font-medium text-gray-700">First Name *</label>
            <TextInput
              type="text"
              v-model="owner.firstName"
              placeholder="Enter First Name"
            />
          </div>

          <div class="flex flex-col gap-y-2">
            <label class="text-sm font-medium text-gray-700">Last Name *</label>
            <TextInput
              type="text"
              v-model="owner.lastName"
              placeholder="Enter Last Name"
            />
          </div>

          <div class="flex flex-col gap-y-2">
            <label class="text-sm font-medium text-gray-700">Date of Birth *</label>
            <TextInput
              type="date"
              dateFormat="DD/MM/YYYY"
              v-model="owner.dateOfBirth"
              placeholder="DD/MM/YYYY"
            />
          </div>

          <div class="flex flex-col gap-y-2">
            <label class="text-sm font-medium text-gray-700">Personal Code *</label>
            <TextInput
              type="text"
              v-model="owner.personalCode"
              placeholder="Enter Personal Code"
            />
          </div>

          <div class="flex flex-col gap-y-2 md:col-span-2">
            <label class="text-sm font-medium text-gray-700">Citizenship *</label>
            <InputSelect
              :list="countries"
              v-model="owner.citizenship"
            >
              <template #selected="{ item }">
                <div class="flex items-center gap-3">
                  <Icon v-if="item" :icon="'flagpack:' + item.code.toLowerCase()" width="32" height="24" />
                  <span v-if="item">{{ item.name }}</span>
                </div>
              </template>
              <template #item="{ item }">
                <Icon :icon="'flagpack:' + item.code.toLowerCase()" width="32" height="24" />
                <span class="ml-3 block truncate">{{ item.name }}</span>
              </template>
            </InputSelect>
          </div>
        </div>

        <!-- Politically Exposed Person -->
        <div class="px-4 md:px-0">
          <h4 class="text-sm font-semibold text-gray-900 mb-3">Politically Exposed Person *</h4>
          <div class="flex flex-col gap-2">
            <label class="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                v-model="owner.isPep"
                class="w-4 h-4 text-blue-500 border-gray-300 rounded focus:ring-blue-500"
              />
              <span class="text-sm text-gray-700">PEP or relatives PEP?</span>
            </label>
            <label class="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                v-model="owner.isUS"
                class="w-4 h-4 text-blue-500 border-gray-300 rounded focus:ring-blue-500"
              />
              <span class="text-sm text-gray-700">A citizen or tax resident of US</span>
            </label>
          </div>
        </div>

        <!-- Shares Voting Rights -->
        <div class="px-4 md:px-0">
          <div class="flex flex-col gap-y-2">
            <label class="text-sm font-medium text-gray-700">Shares Voting Rights (%)</label>
            <TextInput
              type="text"
              v-model="owner.sharesVotingRights"
              placeholder="Enter Shares Voting Rights"
            />
          </div>
        </div>

        <!-- Tax Information -->
        <div class="px-4 md:px-0">
          <h4 class="text-sm font-semibold text-gray-900 mb-4">Tax Information</h4>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-6 px-4 md:px-0">
          <div class="flex flex-col gap-y-2">
            <label class="text-sm font-medium text-gray-700">Tax Country *</label>
            <InputSelect
              :list="countries"
              v-model="owner.taxCountry"
            >
              <template #selected="{ item }">
                <div class="flex items-center gap-3">
                  <Icon v-if="item" :icon="'flagpack:' + item.code.toLowerCase()" width="32" height="24" />
                  <span v-if="item">{{ item.name }}</span>
                </div>
              </template>
              <template #item="{ item }">
                <Icon :icon="'flagpack:' + item.code.toLowerCase()" width="32" height="24" />
                <span class="ml-3 block truncate">{{ item.name }}</span>
              </template>
            </InputSelect>
          </div>

          <div class="flex flex-col gap-y-2">
            <label class="text-sm font-medium text-gray-700">Tax Number *</label>
            <TextInput
              type="text"
              v-model="owner.taxNumber"
              placeholder="Enter Tax Number"
            />
          </div>
        </div>

        <!-- Address Information -->
        <div class="px-4 md:px-0">
          <h4 class="text-sm font-semibold text-gray-900 mb-4">Address Information</h4>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-6 px-4 md:px-0">
          <div class="flex flex-col gap-y-2">
            <label class="text-sm font-medium text-gray-700">Address Line *</label>
            <TextInput
              type="text"
              v-model="owner.addressLine"
              placeholder="Enter Address Line"
            />
          </div>

          <div class="flex flex-col gap-y-2">
            <label class="text-sm font-medium text-gray-700">City *</label>
            <TextInput
              type="text"
              v-model="owner.city"
              placeholder="Enter City"
            />
          </div>

          <div class="flex flex-col gap-y-2">
            <label class="text-sm font-medium text-gray-700">Country *</label>
            <InputSelect
              :list="countries"
              v-model="owner.country"
            >
              <template #selected="{ item }">
                <div class="flex items-center gap-3">
                  <Icon v-if="item" :icon="'flagpack:' + item.code.toLowerCase()" width="32" height="24" />
                  <span v-if="item">{{ item.name }}</span>
                </div>
              </template>
              <template #item="{ item }">
                <Icon :icon="'flagpack:' + item.code.toLowerCase()" width="32" height="24" />
                <span class="ml-3 block truncate">{{ item.name }}</span>
              </template>
            </InputSelect>
          </div>

          <div class="flex flex-col gap-y-2">
            <label class="text-sm font-medium text-gray-700">Postcode/Zip Code *</label>
            <TextInput
              type="text"
              v-model="owner.postcode"
              placeholder="Enter Postcode/Zip Code"
            />
          </div>
        </div>

        <!-- Upload Area -->
        <div class="px-4 md:px-0">
          <div class="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer">
            <IconCloudUpload class="w-12 h-12 mx-auto text-blue-500 mb-3" />
            <p class="text-sm font-medium text-gray-700 mb-1">Click or drag file to this area to upload</p>
            <p class="text-xs text-gray-500">Maximum file size is 10 MB (valid formats: .png, .jpeg, .jpg, .pdf)</p>
            <p class="text-xs text-gray-500 mt-2">We support .png, .jpeg, .jpg and .pdf file formats and size up to 10 MB.</p>
          </div>
        </div>
      </div>

      <!-- Add More Owners Button -->
      <div class="px-4 md:px-0">
        <button
          @click="addOwner"
          class="w-full py-3 border-2 border-dashed border-blue-500 rounded-lg text-blue-500 hover:bg-blue-50 transition-colors font-medium flex items-center justify-center gap-2"
        >
          <span class="text-xl">+</span>
          Add More
        </button>
      </div>

      <!-- Directors -->
      <div v-for="(director) in directors" :key="director.id" class="flex flex-col gap-6 border-t pt-8">
        <div class="px-4 md:px-0">
          <BasicFormTitle>
            Director
          </BasicFormTitle>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-6 px-4 md:px-0">
          <div class="flex flex-col gap-y-2">
            <label class="text-sm font-medium text-gray-700">First Name *</label>
            <TextInput
              type="text"
              v-model="director.firstName"
              placeholder="Enter First Name"
            />
          </div>

          <div class="flex flex-col gap-y-2">
            <label class="text-sm font-medium text-gray-700">Last Name *</label>
            <TextInput
              type="text"
              v-model="director.lastName"
              placeholder="Enter Last Name"
            />
          </div>

          <div class="flex flex-col gap-y-2">
            <label class="text-sm font-medium text-gray-700">Date of Birth *</label>
            <TextInput
              type="date"
              dateFormat="DD/MM/YYYY"
              v-model="director.dateOfBirth"
              placeholder="DD/MM/YYYY"
            />
          </div>

          <div class="flex flex-col gap-y-2">
            <label class="text-sm font-medium text-gray-700">Beneficial Owner *</label>
            <InputSelect
              :list="{}"
              v-model="director.beneficialOwner"
            >
              <template #selected>
                <span class="text-gray-500">Select Beneficial Owner</span>
              </template>
            </InputSelect>
          </div>
        </div>

        <!-- Upload Area -->
        <div class="px-4 md:px-0">
          <div class="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer">
            <IconCloudUpload class="w-12 h-12 mx-auto text-blue-500 mb-3" />
            <p class="text-sm font-medium text-gray-700 mb-1">Click or drag file to this area to upload</p>
            <p class="text-xs text-gray-500">Maximum file size is 10 MB (valid formats: .png, .jpeg, .jpg, .pdf)</p>
            <p class="text-xs text-gray-500 mt-2">We support .png, .jpeg, .jpg and .pdf file formats and size up to 10 MB.</p>
          </div>
        </div>
      </div>

      <!-- Add More Directors Button -->
      <div class="px-4 md:px-0">
        <button
          @click="addDirector"
          class="w-full py-3 border-2 border-dashed border-blue-500 rounded-lg text-blue-500 hover:bg-blue-50 transition-colors font-medium flex items-center justify-center gap-2"
        >
          <span class="text-xl">+</span>
          Add More
        </button>
      </div>

      <!-- Signers -->
      <div v-for="(signer) in signers" :key="signer.id" class="flex flex-col gap-6 border-t pt-8">
        <div class="px-4 md:px-0">
          <BasicFormTitle>
            Signer
          </BasicFormTitle>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-6 px-4 md:px-0">
          <div class="flex flex-col gap-y-2">
            <label class="text-sm font-medium text-gray-700">First Name *</label>
            <TextInput
              type="text"
              v-model="signer.firstName"
              placeholder="Enter First Name"
            />
          </div>

          <div class="flex flex-col gap-y-2">
            <label class="text-sm font-medium text-gray-700">Last Name *</label>
            <TextInput
              type="text"
              v-model="signer.lastName"
              placeholder="Enter Last Name"
            />
          </div>

          <div class="flex flex-col gap-y-2">
            <label class="text-sm font-medium text-gray-700">Date of Birth *</label>
            <TextInput
              type="date"
              dateFormat="DD/MM/YYYY"
              v-model="signer.dateOfBirth"
              placeholder="DD/MM/YYYY"
            />
          </div>

          <div class="flex flex-col gap-y-2">
            <label class="text-sm font-medium text-gray-700">Beneficial Owner *</label>
            <InputSelect
              :list="{}"
              v-model="signer.beneficialOwner"
            >
              <template #selected>
                <span class="text-gray-500">Select Beneficial Owner</span>
              </template>
            </InputSelect>
          </div>
        </div>

        <!-- Upload Area -->
        <div class="px-4 md:px-0">
          <div class="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer">
            <IconCloudUpload class="w-12 h-12 mx-auto text-blue-500 mb-3" />
            <p class="text-sm font-medium text-gray-700 mb-1">Click or drag file to this area to upload</p>
            <p class="text-xs text-gray-500">Maximum file size is 10 MB (valid formats: .png, .jpeg, .jpg, .pdf)</p>
            <p class="text-xs text-gray-500 mt-2">We support .png, .jpeg, .jpg and .pdf file formats and size up to 10 MB.</p>
          </div>
        </div>
      </div>

      <!-- Add More Signers Button -->
      <div class="px-4 md:px-0">
        <button
          @click="addSigner"
          class="w-full py-3 border-2 border-dashed border-blue-500 rounded-lg text-blue-500 hover:bg-blue-50 transition-colors font-medium flex items-center justify-center gap-2"
        >
          <span class="text-xl">+</span>
          Add More
        </button>
      </div>

      <div v-if="isLoading" class="flex items-center justify-center gap-2 text-gray-600 dark:text-gray-400 px-4 md:px-0">
        <div class="animate-spin rounded-full h-5 w-5 border-2 border-blue-500 border-t-transparent"></div>
        <span class="text-sm">Submitting...</span>
      </div>
    </div>
  </CardLayout>
</template>

<style scoped lang="scss">
</style>
